import React, { useEffect, FC, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from 'highcharts/highstock';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import HighchartsBoost from 'highcharts/modules/boost';
import APIutil from "../../../api/APIutils";
import * as url from "../../../api/url";
import { MenuItem, Select, InputLabel } from "@mui/material";
import { getAllResources } from "action/cultureAction";
import { useDispatch, useSelector } from "react-redux";
import { 
  PointSelect, 
  LineChartContainer, 
  LineChartButtons,
  DataPointLoader } from "./styled";
import {
  Identifier,
  DatalogIdentifierOptions,
} from "../ReportsManagement/Identifier";

export interface PropertyRequest {
  equipmentIds: Array<number>;
  buildingId: number;
  familyType: number;
  selectedPoints: Array<string>;
}

export interface tableHeadings {
  title: string;
  intlId: string;
  type: string;
}

export interface PropertyInfo {
  id: string;
  name: string;
  definitionIds: Array<DatalogDefinitions>;
}

export interface DatalogDefinitions {
  objectId: number;
  definitionId: number;
}

export interface SeriesEntry {
  name: string;
  data: Array<any>;
}

export interface SeriesPoint {
  xVal: any;
  yVal: any;
}

export const LineChart: FC<PropertyRequest> = (props) => {
  const { equipmentIds, buildingId, familyType, selectedPoints } = props;
  const [datalogDefs, setDatalogDefs] = useState([] as Array<number>);
  const [selectedProp, setSelectedProp] = useState(selectedPoints && selectedPoints.length > 0 ? selectedPoints[0] : "");
  const [shortListEquipIds, setShortListEquipIds] = useState([] as Array<number>);
  const [equipProperties, setEquipProperties] = useState([] as Array<any>);
  const [seriesCollection, setSeriesCollection] = useState([] as Array<SeriesEntry>)
  const [propertyName, setpropertyName] = useState('');
  const [yAxisTitle, setyAxisTitle] = useState('');
  const [tickInterval, setTickInterval] = useState<any>();
  const [tickMax, setTickMax] = useState<any>();
  const [pointEnums, setPointEnums] = useState([] as Array<any>);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [dataPointsLoader, setDataPointsLoader] = useState(false);
  const [chartOptions, setChartOptions] = useState({});
  const [selectedRange, setSelectedRange]=useState(6 as number);
  
  const dispatch = useDispatch();
  const labels = useSelector(
    (state: any) =>
      state.CulturalLabelsRootReducer.ReportMgmt_LabelsBasedOnPreference
  );
  const compareLabel = labels !== null ? `${labels["Compare.Text"]}` : "Compare";
  const noPlottablePoints = labels !== null ? `${labels["NoPlottablePoints.Text"]}` : "No plottable points";
  const noDataStrLocal = labels !== null ? `${labels["NoDataToDisplay.Text"]}` : "no data";
   
  NoDataToDisplay(Highcharts);
  HighchartsBoost(Highcharts);
 
  useEffect(() => {  
    dispatch(getAllResources());
    setSelectedPeriod("1W");
    getPropertyInfo();

    Highcharts.setOptions({
      lang: {
        noData: noDataStrLocal,
      },
     });
  }, []);

  useEffect(() => {
    getChartData();
  }, [datalogDefs]);

  useEffect(() => {
    let hasData = seriesCollection && seriesCollection.length > 0;
    let d = hasData ? seriesCollection : [];
    let dData = hasData ? seriesCollection[0].data : [];

    setChartOptions({
      credits: {
        enabled: false,
      },
      lang: {
        noData: noDataStrLocal,
      },
      noData: {
        style: {
          fontWeight: "bold",
          fontSize: "15px",
          color: "#303030",
        },
      },
       navigator: {
         adaptToUpdatedData: false,
         enabled: hasData ? true : false,
         series: { data: dData}
       },
      rangeSelector: {
        allButtonsEnabled: hasData,
        buttons: [
          {
            type: "day",
            count: 1,
            text: "1D",
          },
          {
            type: "week",
            count: 1,
            text: "1W",
          },
          {
            type: "week",
            count: 2,
            text: "2W",
            events: {
              click: function () {
                setPeriod("1M");
              },
            },
          },
          {
            type: "month",
            count: 1,
            text: "1M",
            events: {
              click: function () {
                setPeriod("1M");
              },
            },
          },
          {
            type: "month",
            count: 6,
            text: "6M",
            events: {
              click: function () {
                setPeriod("1Y");
              },
            },
          },
          {
            type: "year",
            count: 1,
            text: "1Y",
            events: {
              click: function () {
                setPeriod("1Y");
              },
            },
          },
          {
            type: "all",
            text: "All",
          },
        ],
        selected:selectedRange 
      },
      xAxis: {
        type: "datetime",
        events: {
          setExtremes: function(e) {
            if(hasData && typeof(e.rangeSelectorButton)!== 'undefined') {
              let c = e.rangeSelectorButton.count;
              let t = e.rangeSelectorButton.type;
              let btn_index = 6;
              if(c == 1 && t == "day"){
                btn_index = 0;
              } else if(c == 1 && t == "week"){
                btn_index = 1;
              } else if(c == 2 && t == "week"){
                btn_index = 2;
              } else if(c == 1 && t == "month"){
                btn_index = 3;
              } else if(c == 6 && t == "month"){
                btn_index = 4;
              } else if(c == 1 && t == "year"){
                btn_index = 5;
              } else if(t === "all"){
                btn_index = 6;
              }

              setSelectedRange(btn_index)
            }
          }
        }
      },
      yAxis: [
        {
          title: {
            text: yAxisTitle,
          },
          tickInterval: tickInterval,
          max: tickMax,
          labels: {
            formatter: (value) => {
              let yaxisVal = "";

              if (value && value !== null) {
                if (pointEnums.length > 0 && pointEnums.length >= value.value) {
                  let fdn = pointEnums.find((e) => e.value === value.value);

                  if (fdn) 
                    yaxisVal = fdn.label;

                } else {
                  yaxisVal = value.value;
                }
              }
              return yaxisVal;
            },
          },
        },
      ],
      title: { text: "" },
      plotOptions: {
        series: {
          marker: {
            radius: 1,
          },
         },
      },
      series: d,
    });
  }, [seriesCollection]);

  async function getPropertyInfo() {
    setyAxisTitle("");

    let start: Date = new Date();
    start.setHours(0, 0, 0, 0);

    let end = new Date();
    end.setHours(23, 59, 59, 999);

    let equipIds = equipmentIds.length <= 10 ? equipmentIds : equipmentIds.slice(0, 10);
    setShortListEquipIds(equipIds);

    var equipRequest = {
      ControllerIds: equipIds,
      StartDate: start,
      EndDate: end,
    };

    await APIutil.postData(url.get_Equipment_Properties, equipRequest)
      .then((res) => {
        setEquipProperties(res.data);
        getDefinitionList(res.data, equipIds, selectedProp);
      })
      .catch((error) => {
        throw error;
      });
  }

  function sortPointOptions(listToSort: Array<any>) {

    var items = listToSort.slice();

      items.sort((a, b) => {
        const nameA = a.toUpperCase(); // ignore upper and lowercase
        const nameB = b.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });

    return items;
  }

  function getDefinitionList(properties: Array<PropertyInfo>, equipIds, curProp: string) {
    let defs: Array<number> = [];
    let firstHeading = curProp; 

    if (properties.length > 0 && firstHeading !== "") {
      let fdn = properties.filter((prop) => prop.name === firstHeading);

      if (fdn.length > 0 && fdn[0].definitionIds.length > 0) {

        setDataPointsLoader(true);
        var uniqueDefs : Array<DatalogDefinitions> = [];
        fdn[0].definitionIds.filter(function(item){
          var i = uniqueDefs.findIndex(x => (x.objectId == item.objectId));
          if(i <= -1){
            uniqueDefs.push(item);
          }
          return null;
        });

        uniqueDefs.forEach((p) => {
          if (equipIds.indexOf(p.objectId) >= 0) {
            defs.push(p.definitionId);
          }
        });
      }
    }

    setDatalogDefs(defs);
  }

  async function getChartData() {
    let identifiers: Array<Identifier> = new Array<Identifier>();

    if (datalogDefs.length > 0) {    
      datalogDefs.forEach((defId) => {
        if (defId > 0) {
          let iden = createIdentifier(defId);
          identifiers.push(iden);
        }
      });
    }

    await APIutil.postData(url.get_identifierData_multiple, identifiers)
      .then((res) => {
        setEnums(res.data);
        updateSeries(res.data);
      })
      .catch((error) => {
        throw error;
      });

    setDataPointsLoader(false);   
  }

  function setEnums(data: Array<any>) {
    if (data && data !== null && data.length > 0) {
      let dFirst = data.find((d) => d.isEnumeration === true);

      // these should be left as undefined so that non-enum axis values are not impacted
      let yAxisInterval; 
      let numEnums;
      let ptEnums = [];

      if (dFirst) {
        ptEnums = dFirst.enumeration;
        numEnums = dFirst.enumeration.length;
        yAxisInterval = 1;
      }

      setPointEnums(ptEnums);
      setTickInterval(yAxisInterval);
      setTickMax(numEnums);
    }
  }

  function createIdentifier(defId: number) {
    let idenOps = new DatalogIdentifierOptions();
    idenOps.key = defId;
    idenOps.familyType = familyType;
    idenOps.siteId = buildingId;
    idenOps.reportPeriod = selectedPeriod;

    return Identifier.GeneratePrimaryKeyDatalogIdentifier(idenOps);
  }

  const updateSeries = (data: Array<any>) => {
    let dCollection: Array<SeriesEntry> = [];
    let propertyName : string = "";
    let yTitle: string = "";

    if (data.length > 0) {
      let dFirst = data[0];
      yTitle= dFirst.unitName ? dFirst.unitName + " " + dFirst.symbol : '';
      setyAxisTitle(yTitle);

      data.forEach((d) => {
        let legendName: string = "";

        if (d.series.length > 0) {
          propertyName = d.series[0].affectedProperty;
          legendName = d.series[0].controllerName;
        }

        let sData: Array<any> = [];
        let sEntry = { name: legendName, data: sData };

        if (d.seriesLight.length > 0) {
          d.seriesLight.forEach((sl) => {
            // this will collect all of the string data [number,number]
            let collectionOfPoints: Array<any> = [];

            if (sl.unixDate && sl.value) {
              collectionOfPoints.push(sl.unixDate);
              collectionOfPoints.push(sl.value);
              sEntry.data.push(collectionOfPoints);
            }
          });
          dCollection.push(sEntry);
        }
      });

      setpropertyName(propertyName); // this is for the chart title which we currently aren't displaying
      setSeriesCollection(dCollection);
    } else if (seriesCollection.length !== 0) {
      setyAxisTitle("no data");
      setSeriesCollection(dCollection);
    }
  };

  const onChangeSelect = (value: string) => {
    setSelectedProp(value);
    setyAxisTitle("");

    if (shortListEquipIds && shortListEquipIds.length > 0 && equipProperties && equipProperties.length > 0) {
      getDefinitionList(equipProperties, shortListEquipIds, value);
    } else {
      getPropertyInfo();
    }
   };

   function setPeriod(chartSelectedPeriod: string) {
    let changeDataForMonth: boolean = chartSelectedPeriod === "1M" && selectedPeriod !== "1M" && selectedPeriod !== "1Y";
    let changeDataForYear: boolean = chartSelectedPeriod === "1Y" && selectedPeriod !== "1Y";

    if (changeDataForMonth || changeDataForYear) {
      setSelectedPeriod(chartSelectedPeriod);
      getPropertyInfo();
    } 
   }

   function displayOptions() {
    let items : Array<any> = sortPointOptions(selectedPoints);
    return items.map((td, i) => {
      return (
        <MenuItem
          value={td}
          key={td}
          selected
        >
          {td}
        </MenuItem>
      );
    });
  }

  return (
    <div>
      <LineChartButtons>
      {
        selectedPoints.length === 0 ? <span>{noPlottablePoints}</span> :
        <PointSelect>
           <InputLabel 
            sx={{
                color: "#323232"
              }} 
              id="point-select-label">
                {compareLabel}
          </InputLabel>
          <Select
            id="compareDD"
            value={selectedProp}
            displayEmpty={true}
            onChange={e => onChangeSelect(e.target.value)}
            sx={{
              height: "36px",
            }}
            >
              {displayOptions()}
          </Select>
        </PointSelect>
        }
        {
          dataPointsLoader ? <DataPointLoader title="Fetching data points">
            <div className="spinner" aria-busy="true">
              <span className="bubble1"></span>
              <span className="bubble2"></span>
              <span className="bubble3"></span>
            </div>
          </DataPointLoader> : null
        }
      </LineChartButtons>

      <LineChartContainer>
        <HighchartsReact highcharts={Highcharts} constructorType={'stockChart'} options={chartOptions} />
      </LineChartContainer>

    </div>
  );
};

export default LineChart;
